import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Sparkles,
  Crown,
  Zap,
  Check,
  ArrowRight,
  Lock,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import ManageSubscriptionModal from './ManageSubscriptionModal';
import {
  FeatureKey,
  PlanTier,
  getFeatureDefinition,
  nextTierAbove,
  tierIncludes,
  tierLabel,
} from '../../business/billing/featureCatalog';

/**
 * Reason the upgrade modal was shown — drives copy and which tier is highlighted.
 */
export type UpgradeReason = 'feature' | 'limit' | 'noSubscription';

export interface UpgradePromptConfig {
  /** Why we're showing the modal — informs copy and tier targeting. */
  reason?: UpgradeReason;
  /** Feature key from `FEATURE_CATALOG` — auto-fills tier + copy. */
  feature?: FeatureKey | string;
  /** Override the modal title (defaults to feature-driven copy). */
  title?: string;
  /** Override the modal description. */
  description?: string;
  /** Override the tier the user is being upsold to. Defaults to feature's required tier or next-above. */
  targetTier?: PlanTier;
  /** The tier the user is currently on — pulled from PlanContext when omitted. */
  currentTier?: PlanTier | null;
  /** Free-form contextual note (e.g. "You've used 5 of 5 booking forms"). */
  contextNote?: string;
  /** Called after a successful plan change. */
  onUpgraded?: () => void;
  /** Called on dismiss/close. */
  onClose?: () => void;
}

interface UpgradeModalProps extends UpgradePromptConfig {
  isOpen: boolean;
  /** Clears provider-held config after the overlay and any nested checkout modal finish. */
  onPromptComplete?: () => void;
}

interface TierPresentation {
  name: PlanTier;
  monthlyPrice: number;
  annualPrice: number;
  tagline: string;
  highlights: string[];
  gradient: string;
  icon: React.ReactNode;
}

const TIER_PRESENTATION: Record<PlanTier, TierPresentation> = {
  Solo: {
    name: 'Starter',
    monthlyPrice: 49,
    annualPrice: 39,
    tagline: 'For small teams',
    highlights: [
      'Unlimited bookings',
      '1 user + 1 Team Member included',
      '2 booking forms',
      'Google Calendar',
      'SMS/Email reminders',
      'Radius territory only',
    ],
    gradient: 'from-amber-500 to-orange-600',
    icon: <Zap className="w-5 h-5" />,
  },
  Pro: {
    name: 'Pro',
    monthlyPrice: 99,
    annualPrice: 79,
    tagline: 'For teams',
    highlights: [
      'Unlimited bookings',
      '2 users included',
      '5 booking forms',
      'Chatbot',
      'Zapier integration',
      'Smart Fill, Add a Stop, Plan a Stop',
      'Smart Assignment',
      'Custom Territories',
      'Form Pre-qualification',
      'AI Enhance Route',
    ],
    gradient: 'from-blue-500 to-blue-600',
    icon: <Sparkles className="w-5 h-5" />,
  },
  Scale: {
    name: 'Scale',
    monthlyPrice: 199,
    annualPrice: 159,
    tagline: 'For growing operations',
    highlights: [
      'Everything in Pro',
      '4 users included',
      'Unlimited booking forms',
      'Voice Agent',
      'Priority chat support',
      'Setup session included',
    ],
    gradient: 'from-emerald-500 to-blue-600',
    icon: <Crown className="w-5 h-5" />,
  },
};

function resolveTargetTier(config: UpgradePromptConfig): PlanTier {
  if (config.targetTier) return config.targetTier;
  if (config.feature) {
    const def = getFeatureDefinition(config.feature);
    if (config.currentTier && tierIncludes(config.currentTier, def.requiredTier)) {
      return nextTierAbove(config.currentTier) ?? def.requiredTier;
    }
    return def.requiredTier;
  }
  return nextTierAbove(config.currentTier ?? null) ?? 'Pro';
}

function resolveCopy(config: UpgradePromptConfig): { title: string; description: string; bullets?: string[] } {
  if (config.title || config.description) {
    return {
      title: config.title ?? 'Upgrade your plan',
      description: config.description ?? "You'll unlock more of Routemize on a higher plan.",
    };
  }
  if (config.feature) {
    const def = getFeatureDefinition(config.feature);
    return {
      title: `${def.label} is a ${def.requiredTier} feature`,
      description: def.description,
      bullets: def.bullets,
    };
  }
  if (config.reason === 'limit') {
    return {
      title: "You've hit a plan limit",
      description: 'Upgrade to keep going without disruption.',
    };
  }
  if (config.reason === 'noSubscription') {
    return {
      title: 'Pick a plan to get started',
      description: 'Choose the plan that fits your team to unlock Routemize.',
    };
  }
  return {
    title: 'Upgrade to unlock this',
    description: "You'll get this feature plus everything else on the higher plan.",
  };
}

/**
 * Reusable plan upgrade modal. Renders the recommended tier card, deep-links
 * into `ManageSubscriptionModal` for the actual checkout flow, and offers a
 * "Compare all plans" opens the plan comparison tab in `ManageSubscriptionModal`.
 *
 * Driven by `UpgradePromptProvider` — call `useUpgradePrompt().showUpgrade(...)`
 * to trigger from any component.
 */
const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onPromptComplete, ...config }) => {
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageModalTab, setManageModalTab] = useState<'overview' | 'change'>('change');

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') config.onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, config]);

  if (!isOpen && !showManageModal) return null;

  const targetTier = resolveTargetTier(config);
  const tierInfo = TIER_PRESENTATION[targetTier];
  const { title, description, bullets } = resolveCopy(config);
  const isLimitReason = config.reason === 'limit';

  const headerIcon = isLimitReason ? (
    <TrendingUp className="h-5 w-5" />
  ) : config.reason === 'noSubscription' ? (
    <Sparkles className="h-5 w-5" />
  ) : (
    <Lock className="h-5 w-5" />
  );

  const headerBadge = config.feature && !isLimitReason && config.reason !== 'noSubscription' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-100">
      {tierLabel(targetTier)} feature
    </span>
  ) : null;

  const finishPrompt = () => {
    onPromptComplete?.();
  };

  const dismissUpgradeOverlay = () => {
    config.onClose?.();
  };

  const handleClose = () => {
    dismissUpgradeOverlay();
    if (!showManageModal) {
      finishPrompt();
    }
  };

  const openManageModal = (tab: 'overview' | 'change') => {
    setManageModalTab(tab);
    setShowManageModal(true);
    dismissUpgradeOverlay();
  };

  const handleViewAllPlans = () => {
    openManageModal('change');
  };

  const handleUpgradeClick = () => {
    openManageModal('change');
  };

  const handleManageClose = () => {
    setShowManageModal(false);
    finishPrompt();
  };

  const handleSubscriptionUpdated = () => {
    setShowManageModal(false);
    config.onUpgraded?.();
    dismissUpgradeOverlay();
    finishPrompt();
  };

  const features = bullets && bullets.length > 0 ? bullets : tierInfo.highlights;

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)' }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-lg bg-surface rounded-2xl shadow-2xl overflow-hidden animate-[fadeInUp_180ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-b border-ink-100">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 text-ink-400 hover:text-ink-600 hover:bg-surface/60 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-sm">
              {headerIcon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-ink-900 leading-tight">{title}</h2>
                {headerBadge}
              </div>
              <p className="mt-1 text-sm text-ink-600 leading-snug">{description}</p>
            </div>
          </div>

          {config.contextNote && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">{config.contextNote}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
              Recommended upgrade
            </span>
            {config.currentTier && (
              <span className="text-11 text-ink-500">
                Currently on <span className="font-medium text-ink-700">{config.currentTier}</span>
              </span>
            )}
          </div>

          <div className="relative rounded-xl border-2 border-blue-500 overflow-hidden bg-surface">
            <div className={`px-5 py-4 bg-gradient-to-br ${tierInfo.gradient} text-white`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {tierInfo.icon}
                  </div>
                  <div>
                    <div className="text-base font-semibold leading-tight">{tierInfo.name}</div>
                    <div className="text-xs text-white/85 leading-tight">{tierInfo.tagline}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold leading-none">${tierInfo.annualPrice}</div>
                  <div className="text-2xs text-white/85 mt-1">/mo billed annually</div>
                </div>
              </div>
            </div>

            <div className="px-5 py-4">
              <ul className="space-y-2">
                {features.map((highlight) => (
                  <li key={highlight} className="flex items-start gap-2 text-sm text-ink-700">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-ink-400">*$39 per additional seat</p>
            </div>
          </div>

          <button
            onClick={handleUpgradeClick}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-all"
          >
            Upgrade to {tierInfo.name}
            <ArrowRight className="h-4 w-4" />
          </button>

          <div className="mt-3 flex items-center justify-between text-xs">
            <button
              onClick={handleViewAllPlans}
              className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
            >
              Compare all plans
            </button>
            <button
              onClick={handleClose}
              className="text-ink-500 hover:text-ink-700"
            >
              Not now
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );

  return (
    <>
      {isOpen && createPortal(modal, document.body)}
      {showManageModal && (
        <ManageSubscriptionModal
          isOpen={showManageModal}
          onClose={handleManageClose}
          onSubscriptionUpdated={handleSubscriptionUpdated}
          initialTab={manageModalTab}
        />
      )}
    </>
  );
};

export default UpgradeModal;
